import { CreateFolder, RenameObject } from '@/modules/Drive/services'
import { ErrorResponse } from '@/types/shared'
import { t } from 'i18next'
import { Button, FormItem } from 'vnetwork-ui'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { nameSchema } from './schema'
import useAuthStore from '@/stores/useAuthStore'
import { ObjectType, RenameObjectParams } from '@/modules/Drive/types'
import { useParams } from 'react-router-dom'
import {
    extractExtension,
    extractNameAndExtension,
} from '@/modules/Drive/utils'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface ModifyObjectFormProps {
    onClose: () => void
    placeholder: string
    initialValue?: string
    source?: string
    createPrefix?: string
    type?: ObjectType
}

interface FormFields {
    objectName: string
}

const ModifyObjectForm: React.FC<ModifyObjectFormProps> = ({
    onClose,
    placeholder,
    initialValue,
    source,
    type,
    createPrefix,
}) => {
    const { handleSubmit, control } = useForm<FormFields>({
        defaultValues: {
            objectName:
                type == ObjectType.FILE
                    ? extractNameAndExtension(initialValue)
                    : initialValue,
        },
        resolver: yupResolver(nameSchema),
    })

    const queryClient = useQueryClient()
    const employee = useAuthStore((state) => state.employee)
    const { '*': pathParam } = useParams()

    const getCurrentPath = (source?: string) => {
        if (source) {
            return source.replace(/\/$/, '').split('/').slice(0, -1).join('/')
        }
        let prefix = ''
        if (pathParam && pathParam.length > 0) {
            prefix = pathParam
        } else if (employee?.employee_uuid) {
            prefix = String(employee.employee_uuid)
        }
        if (prefix.startsWith('pub/drives/')) {
            prefix = `drives/${prefix.slice('pub/drives/'.length)}`
        } else if (!prefix.startsWith('drives/')) {
            prefix = `drives/${prefix.replace(/^\/+/, '')}`
        }
        prefix = prefix.replace(/\/{2,}/g, '/')
        return prefix
    }

    const handleMutationSuccess = (message: string) => {
        if (createPrefix) {
            queryClient.invalidateQueries({
                queryKey: [createPrefix],
            })
        }

        if (employee?.employee_uuid) {
            queryClient.invalidateQueries({
                queryKey: ['my-drive', employee.employee_uuid],
            })
        } else {
            queryClient.invalidateQueries({ queryKey: ['my-drive'] })
        }
        if (pathParam && pathParam.length > 0) {
            const queryKey = 'pub/drives/' + pathParam
            queryClient.invalidateQueries({
                queryKey: ['folder-explorer', queryKey],
            })
        }
        toast.success(message)
        onClose()
    }

    const handleMutationError = (error: AxiosError<ErrorResponse>) => {
        const errorMessage =
            error?.response?.data?.error_description || t('something-wrong')
        let conflictMessage
        switch (error?.response?.status) {
            case 403:
                conflictMessage = t('drive.no-modify-permission')
                break
            default:
                conflictMessage =
                    error?.response?.data?.error + '' === '409'
                        ? type === ObjectType.FOLDER
                            ? t('folder-existed')
                            : t('file-existed')
                        : errorMessage
                break
        }
        toast.error(conflictMessage)
    }

    const { mutateAsync: createFolderMutation, isPending: createPending } =
        useMutationWithHandler(
            {
                mutationFn: ({
                    objectName,
                    source,
                }: {
                    objectName: string
                    source: string
                }) => CreateFolder(source, objectName),
            },
            {
                reactQueryKeys: [
                    QUERY_KEYS.DRIVE,
                    QUERY_KEYS.DRIVE_FOLDER_EXPLORER,
                ],
                onSuccess: () => {
                    handleMutationSuccess(t('create-folder-success'))
                },
                onError: (error: AxiosError<ErrorResponse>) => {
                    handleMutationError(error)
                },
            }
        )

    const { mutateAsync: renameObjectMutation, isPending: modifyPending } =
        useMutationWithHandler(
            {
                mutationFn: ({
                    old_object_key,
                    new_object_key,
                }: RenameObjectParams) =>
                    RenameObject({ old_object_key, new_object_key }),
            },
            {
                reactQueryKeys: [
                    QUERY_KEYS.DRIVE,
                    QUERY_KEYS.DRIVE_FOLDER_EXPLORER,
                    QUERY_KEYS.DRIVE_STARRED_LIST,
                    QUERY_KEYS.DRIVE_RECENT,
                ],
                successMessage: t('rename-success'),
                onSuccess: () => {
                    handleMutationSuccess(t('rename-success'))
                },
                onError: (error: AxiosError<ErrorResponse>) => {
                    handleMutationError(error)
                },
            }
        )
    const onSubmit: SubmitHandler<FormFields> = async ({ objectName }) => {
        if (source) {
            if (!employee?.employee_uuid) {
                toast.error(t('try-again'))
                return
            }
            let dest = `${getCurrentPath(source)}/${objectName}`
            if (type == ObjectType.FILE) {
                dest = `${dest}.${extractExtension(initialValue)}`
            }
            const payload: RenameObjectParams = {
                old_object_key: source,
                new_object_key: dest,
            }
            await renameObjectMutation(payload)
        } else {
            await createFolderMutation({
                objectName,
                source: createPrefix || getCurrentPath(),
            })
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex items-center gap-2 mt-4">
                <div className="flex-1">
                    <FormItem
                        control={control}
                        name="objectName"
                        type="textfield"
                        placeholder={placeholder}
                    />
                </div>
                {type == ObjectType.FILE && (
                    <div className="bg-gray-100 px-3 py-1 rounded-md">
                        .{extractExtension(initialValue)}
                    </div>
                )}
            </div>
            <div className="flex items-center justify-end col-span-2 gap-2 mt-2">
                <Button
                    disabled={createPending || modifyPending}
                    onClick={() => {
                        onClose()
                    }}
                    variant="outlined"
                >
                    {t('cancel')}
                </Button>
                <Button
                    disabled={createPending || modifyPending}
                    loading={createPending || modifyPending}
                    type="submit"
                >
                    {t('create-folder-confirm')}
                </Button>
            </div>
        </form>
    )
}

export default ModifyObjectForm
